import { UTCDate } from '@date-fns/utc';
import { differenceInDays, differenceInYears } from 'date-fns';
import { z } from 'zod';
import { getBaseOasisValidationSchema } from '../../oasis';
import { SectionA } from '../../oasis-sections/section-a';
import { SectionN } from '../../oasis-sections/section-n';
import { OASIS } from '../../oasis-types';
import { fieldSetValidate, format, isActive, isTimepoint } from '../util';
/**
 *
 * Note: if there's an overlapping error for a particular field this is the order in which they'll be displayed
 * form level
 * section level
 * field level
 * base level: same level as "field", but uses built-in zod validations
 */
export const sectionAConsistency = ({ input, ctx, version, isSection, field }) => {
    const schema = getBaseOasisValidationSchema('e1', 'soc'); // args here doesn't have a purpose
    if (!isSection && !field) {
        // form level
        const payload = input;
        if (isTimepoint([OASIS.DSCH], payload.SectionA?.m0100)) {
            // 4480 - If M0100_ASSMT_REASON=[09] then M2020_CRNT_MGMT_ORAL_MDCTN cannot equal [^]
            if (isActive(SectionN.m2020, version, payload.SectionA?.m0100) && !payload.SectionN?.m2020) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-4480 M2020 cannot be empty',
                    path: ['SectionN', 'm2020'],
                });
            }
        }
    }
    else if (!field) {
        // section level
        const payload = input;
        // A-consistency-4570 If M0100_ASSMT_REASON=[09], then if M2410_INPAT_FACILITY is active it must equal [NA].
        if (isTimepoint([OASIS.DSCH], payload?.m0100) && isActive(SectionA.m2410, version, payload?.m0100) && payload?.m2410 !== 'NA') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: '-4570 M2410 must equal NA.',
                path: ['m2410'],
            });
        }
        // A-consistency-4580 If M0100_ASSMT_REASON=[06,07], then if M2410_INPAT_FACILITY is active it must not equal [NA].
        if (isTimepoint([OASIS.TR, OASIS.TRD], payload?.m0100) &&
            isActive(SectionA.m2410, version, payload?.m0100) &&
            payload?.m2410 === 'NA') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: '-4580 M2410 must not equal NA.',
                path: ['m2410'],
            });
        }
        if (isActive(SectionA.a2123, version, payload?.m0100) && isActive(SectionA.a2124, version, payload?.m0100)) {
            // A-consistency-5780 (a) If A2123=[0], then all active items from A2124A through A2124E must equal [^].
            if (payload?.a2123 === '0' && payload.a2124?.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5780 (a) If A2123 = 0, then all items in A2124 from A through E must NOT be checked.',
                    path: ['a2123'],
                });
            }
            // A-consistency-5780 (b) If A2123=[1], then all active item from A2124A through A2124E must not equal [^].
            if (payload?.a2123 === '1' && !payload.a2124?.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5780 (b) If A2123 = 1, then all items in A2124 from A through E must NOT be empty.',
                    path: ['a2123'],
                });
            }
            // A-consistency-5780 (c) If A2123=[1], then at least one active item from A2124A through A2124E must equal [1].
            if (payload?.a2123 === '1' && !payload.a2124?.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5780 (c) If A2123 = 1, then at least one item in A2124 from A through E must be checked.',
                    path: ['a2123'],
                });
            }
        }
        // A-consistency-3470 If M0018_PHYSICIAN_UK=[0], then if M0018_PHYSICIAN_ID is active it cannot equal [^].
        if (!payload?.m0018NA && !payload?.m0018) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-3470 Please enter physician's NPI if known, otherwise select 'Attending physician NPI: Unknown'`,
                path: ['m0018'],
            });
        }
        // A-consistency-3480 If M0018_PHYSICIAN_UK=[1], then if M0018_PHYSICIAN_ID is active it must equal [^].
        if (payload?.m0018NA && payload?.m0018) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "-3480 If physician's NPI is NOT known, then M0018 must be empty",
                path: ['m0018'],
            });
        }
        if (isTimepoint([OASIS.SOC], payload?.m0100)) {
            // A-consistency-3280 If M0100_ASSMT_REASON=[01] and if M0030_START_CARE_DT and M1005_INP_DISCHARGE_DT are both active and contain valid dates (not [^]), then M0030_START_CARE_DT minus M1005_INP_DISCHARGE_DT should be greater than or equal to zero and less than or equal to 14 days.
            if (isTimepoint([OASIS.SOC], payload?.m0100) && isActive(SectionA.m0030, version, payload?.m0100)) {
                if (payload?.m0030 && payload?.m1005) {
                    const dateDiff = differenceInDays(new UTCDate(payload.m0030), new UTCDate(payload.m1005));
                    if (dateDiff < 0 || dateDiff > 14) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `-3280 The difference between M0030 and M1005 [${format(payload.m1005)}] should be greater than or equal to zero and less than or equal to 14 days. Difference ${dateDiff} days`,
                            path: ['m0030'],
                        });
                    }
                }
            }
            // A-consistency-3300 If M0100_ASSMT_REASON=[01] and if M0090_INFO_COMPLETED_DT and M0030_START_CARE_DT are both active and contain valid dates (not [^]), then M0090_INFO_COMPLETED_DT minus M0030_START_CARE_DT should be greater than or equal to zero and less than or equal to 5.
            if (isTimepoint([OASIS.SOC], payload?.m0100) && isActive(SectionA.m0030, version, payload?.m0100)) {
                if (payload?.m0090 && payload?.m0030) {
                    const dateDiff = differenceInDays(new UTCDate(payload.m0090), new UTCDate(payload.m0030));
                    if (dateDiff < 0 || dateDiff > 5) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `-3300 The difference between M0090 and M0030 [${format(payload.m0030)}] should be greater than or equal to zero and less than or equal to 5. Difference ${dateDiff} days`,
                            path: ['m0090'],
                        });
                    }
                }
            }
            // A-consistency-5340 If M0104_PHYSN_RFRL_DT is not equal to [^] and if M0100_ASSMT_REASON=[01], then M0104_PHYSN_RFRL_DT must be less than or equal to M0030_START_CARE_DT and must be greater than M0066_PAT_BIRTH_DT.
            if (isTimepoint([OASIS.SOC]) && isActive(SectionA.m0104, version, payload?.m0100)) {
                if (payload?.m0104 && payload?.m0030 && payload?.m0066) {
                    const dateDiffM0030 = differenceInDays(new UTCDate(payload.m0104), new UTCDate(payload.m0030));
                    const dateDiffM0066 = differenceInDays(new UTCDate(payload.m0104), new UTCDate(payload.m0066));
                    if (dateDiffM0030 > 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `-5340 M0104 must be less than or equal to M0030[${format(payload.m0030)}]. Difference ${dateDiffM0030} days`,
                            path: ['m0104'],
                        });
                    }
                    if (dateDiffM0066 <= 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `-5340 M0104 must be greater than M0066 [${format(payload.m0066)}]. Difference ${dateDiffM0066} days`,
                            path: ['m0104'],
                        });
                    }
                }
            }
        }
        // A-consistency-3490 If M0032_ROC_DT_NA=[0], then if M0032_ROC_DT is active it must not equal [^].
        if (!payload?.m0032NA && !payload?.m0032) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-3490 Please enter resumption of care date if known, otherwise select 'No resumption of care date'`,
                path: ['m0032'],
            });
        }
        // A-consistency-3500 If M0032_ROC_DT_NA=[1], then if M0032_ROC_DT is active it must equal [^].
        if (payload?.m0032NA && payload?.m0032) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-3500 If resumption of care date is NOT known, then M0032 must be empty.`,
                path: ['m0032'],
            });
        }
        // A-consistency-4730 If M0100_ASSMT_REASON=[01], then M0032_ROC_DT_NA must be [1].
        if (isTimepoint([OASIS.SOC], payload?.m0100) && !payload?.m0032NA) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-4730 Resumption of care date cannot be known during the Start of Care`,
                path: ['m0032'],
            });
        }
        // A-consistency-4740 If M0100_ASSMT_REASON=[03], then M0032_ROC_DT_NA must be [0].
        if (isTimepoint([OASIS.ROC], payload?.m0100) && payload?.m0032NA) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-4740 Resumption of Care date must be known`,
                path: ['m0032'],
            });
        }
        // A-consistency-3510 If M0063_MEDICARE_NA=[0], then if M0063_MEDICARE_NUM is active it must not equal [^].
        if (!payload?.['m0063-m0065']?.m0063NA && !payload?.['m0063-m0065']?.m0063) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-3510 Please enter Medicare Number if known, otherwise select 'Unknown / Not Available'`,
                path: ['m0063-m0065', 'm0063'],
            });
        }
        // A-consistency-3520 If M0063_MEDICARE_NA=[1], then if M0063_MEDICARE_NUM is active it must equal [^].
        if (payload?.['m0063-m0065']?.m0063NA && payload?.['m0063-m0065']?.m0063) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-3520 If Medicare Number is NOT known, then M0063 must be empty.`,
                path: ['m0063-m0065', 'm0063'],
            });
        }
        // A-consistency-3530 If M0064_SSN_UK=[0], then if M0064_SSN is active it must not equal [^].
        if (!payload?.['m0063-m0065']?.m0064NA && !payload?.['m0063-m0065']?.m0064) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-3530 Please enter SSN if known, otherwise select 'Unknown / Not Available'`,
                path: ['m0063-m0065', 'm0064'],
            });
        }
        // A-consistency-3540 If M0064_SSN_UK=[1], then if M0064_SSN is active it must equal [^].
        if (payload?.['m0063-m0065']?.m0064NA && payload?.['m0063-m0065']?.m0064) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-3540 If SSN is NOT known, then M0064 must be empty.`,
                path: ['m0063-m0065', 'm0064'],
            });
        }
        // A-consistency-3550 If M0065_MEDICAID_NA=[0], then if M0065_MEDICAID_NUM is active it must not equal [^].
        if (!payload?.['m0063-m0065']?.m0065NA && !payload?.['m0063-m0065']?.m0065) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-3550 Please enter Medicaid Number if known, otherwise select 'Unknown / Not Available'`,
                path: ['m0063-m0065', 'm0065'],
            });
        }
        // A-consistency-3560 If M0065_MEDICAID_NA=[1], then if M0065_MEDICAID_NUM is active it must equal [^].
        if (payload?.['m0063-m0065']?.m0065NA && payload?.['m0063-m0065']?.m0065) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-3560 If Medicaid Number is NOT known, then M0065 must be empty.`,
                path: ['m0063-m0065', 'm0065'],
            });
        }
        // A-consistency-3290 If M0100_ASSMT_REASON=[03] and if M0032_ROC_DT and M1005_INP_DISCHARGE_DT are both active and contain valid dates (not [^]), then M0032_ROC_DT minus M1005_INP_DISCHARGE_DT should be greater than or equal to zero and less than or equal to 14.
        if (isTimepoint([OASIS.ROC], payload?.m0100) &&
            isActive(SectionA.m1005, version, payload?.m0100) &&
            isActive(SectionA.m0032, version, payload?.m0100)) {
            if (payload?.m0032 && payload?.m1005) {
                const dateDiff = differenceInDays(new UTCDate(payload.m0032), new UTCDate(payload.m1005));
                if (dateDiff < 0 || dateDiff > 14) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `-3290 The difference between M0032 and M1005 [${format(payload.m1005)}] should be greater than or equal to zero and less than or equal to 14. Difference ${dateDiff} days`,
                        path: ['m0032'],
                    });
                }
            }
        }
        // A-consistency-5290 If M0100_ASSMT_REASON=[06,07,08,09], then M0906_DC_TRAN_DTH_DT must not equal [^].
        if (isTimepoint([OASIS.TR, OASIS.TRD, OASIS.DTH, OASIS.DSCH], payload?.m0100) &&
            isActive(SectionA.m0906, version, payload?.m0100)) {
            if (!payload?.m0906) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-5290 M0906 must NOT be empty.`,
                    path: ['m0906'],
                });
            }
        }
        // If m0030 and m0906 are present and timepoint is one of ['dsch', 'dth', 'tr', 'trd'], then m0906 - m0030 must be <= 59 days
        if (isTimepoint([OASIS.TR, OASIS.TRD, OASIS.DTH, OASIS.DSCH], payload?.m0100) &&
            isActive(SectionA.m0906, version, payload?.m0100)) {
            if (payload?.m0906 && payload.m0030) {
                const dateDiff = differenceInDays(new UTCDate(payload.m0906), new UTCDate(payload.m0030));
                if (dateDiff > 59) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `The difference between M0906 (${format(payload.m0906)}) and M0030 (${format(payload.m0030)}) must not exceed 59 days. Difference: ${dateDiff} days`,
                        path: ['m0906'],
                    });
                }
            }
        }
        // A-consistency-5300 If TRANS_TYPE_CD=[3] (inactivate existing record) and M0100_ASSMT_REASON=[01,03,04,05], then M0906_DC_TRAN_DTH_DT must equal [^].
        // A-consistency-3310 If M0100_ASSMT_REASON=[03] and if M0090_INFO_COMPLETED_DT and M0032_ROC_DT are both active and contain valid dates (not [^]), then M0090_INFO_COMPLETED_DT minus M0032_ROC_DT should be greater than or equal to zero and less than or equal to 2.
        if (isTimepoint([OASIS.ROC], payload?.m0100) &&
            isActive(SectionA.m0090, version, payload?.m0100) &&
            isActive(SectionA.m0032, version, payload?.m0100)) {
            if (payload?.m0090 && payload?.m0032) {
                const dateDiff = differenceInDays(new UTCDate(payload.m0090), new UTCDate(payload.m0032));
                if (dateDiff < 0 || dateDiff > 2) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `-3310 The difference between M0090 and M0032 [${format(payload.m0032)}] should be greater than or equal to zero and less than or equal to 2. Difference ${dateDiff} days`,
                        path: ['m0090'],
                    });
                }
            }
        }
        // A-consistency-3320 If M0100_ASSMT_REASON=[09] and if M0090_INFO_COMPLETED_DT and M0906_DC_TRAN_DTH_DT are both active and contain valid dates (not [^]), then M0090_INFO_COMPLETED_DT minus M0906_DC_TRAN_DTH_DT should be greater than or equal to zero and less than or equal to 2.
        if (isTimepoint([OASIS.DSCH], payload?.m0100) &&
            isActive(SectionA.m0090, version, payload?.m0100) &&
            isActive(SectionA.m0906, version, payload?.m0100)) {
            if (payload?.m0100 === OASIS.DSCH && payload.m0090 && payload.m0906) {
                const dateDiff = differenceInDays(new UTCDate(payload.m0090), new UTCDate(payload.m0906));
                if (dateDiff < 0 || dateDiff > 2) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `-3320 The difference between M0090 and M0906 [${format(payload.m0906)}] should be greater than or equal to zero and less than or equal to 2. Difference ${dateDiff} days`,
                        path: ['m0090'],
                    });
                }
            }
        }
        // A-consistency-5350 If M0104_PHYSN_RFRL_DT is not equal to [^] and if M0100_ASSMT_REASON=[03], then M0104_PHYSN_RFRL_DT must be less than or equal to M0032_ROC_DT and must be greater than M0066_PAT_BIRTH_DT.
        if (isTimepoint([OASIS.ROC], payload?.m0100) &&
            isActive(SectionA.m0104, version, payload?.m0100) &&
            isActive(SectionA.m0032, version, payload?.m0100) &&
            isActive(SectionA.m0032, version, payload?.m0100)) {
            if (payload?.m0104 && payload.m0032 && payload.m0066) {
                const dateDiffM0032 = differenceInDays(new UTCDate(payload.m0104), new UTCDate(payload.m0032));
                const dateDiffM0066 = differenceInDays(new UTCDate(payload.m0104), new UTCDate(payload.m0066));
                if (dateDiffM0032 > 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `-5350 M0104 must be less than or equal to M0032 [${format(payload.m0032)}]. Difference ${dateDiffM0032} days`,
                        path: ['m0104'],
                    });
                }
                if (dateDiffM0066 <= 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `-5350 M0104 must be greater than M0066 [${format(payload.m0066)}]. Difference ${dateDiffM0066} days`,
                        path: ['m0104'],
                    });
                }
            }
        }
        if (isActive(SectionA.m0102, version, payload?.m0100) && !isTimepoint([OASIS.SOC, OASIS.ROC], payload?.m0100)) {
            // A-consistency-3570 If M0102_PHYSN_ORDRD_SOCROC_DT is not equal to [^] (contains a valid date), then if M0102_PHYSN_ORDRD_SOCROC_DT_NA is active it must equal [0,^].
            if (payload?.m0102 && payload?.m0102NA) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-3570 Please enter order date if known, otherwise select 'NA'`,
                    path: ['m0102'],
                });
            }
            // A-consistency-3580 If M0102_PHYSN_ORDRD_SOCROC_DT=[^], then if M0102_PHYSN_ORDRD_SOCROC_DT_NA is active it must equal [1].
            if (!payload?.m0102 && !payload?.m0102NA) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-3580 If order date is NOT known, then 'NA' must be checked.`,
                    path: ['m0102'],
                });
            }
        }
        // A-consistency-4860 If M0150_CPAY_MCAID_FFS=[1], then M0065_MEDICAID_NUM must not equal [^].
        if (payload?.m0150?.includes('CPAY_MCAID_FFS') && !payload['m0063-m0065']?.m0065) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-4860 If 'Medicaid (traditional fee-for-service)' is checked, then M0065 Medicaid Number must NOT be empty`,
                path: ['m0063-m0065', 'm0065'],
            });
        }
        // A-consistency-4850 If M0150_CPAY_MCARE_FFS=[1], then M0063_MEDICARE_NUM must not equal [^].
        if (payload?.m0150?.includes('CPAY_MCARE_FFS') && !payload['m0063-m0065']?.m0063) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `-4850 If 'Medicare (traditional fee-for-service)' is checked, then M0063 Medicare Number must NOT be empty`,
                path: ['m0063-m0065', 'm0063'],
            });
        }
        // A-consistency-5460 The value for M0090_INFO_COMPLETED_DT must be within the allowable date range specified by the value for SPEC_VRSN_CD. The list of SPEC_VRSN_CD values and their corresponding date ranges are provided in the Overview document for the OASIS Data Specifications.
        // A-consistency-3170 The value submitted for M0010_CCN (provider's CMS Certification Number -- CCN) will be compared with the value that is currently in the National Database.  If the values do not match, a warning will be issued.
        if (isActive(SectionA.m0102, version, payload?.m0100) &&
            isActive(SectionA.m0104, version, payload?.m0100) &&
            isTimepoint([OASIS.SOC, OASIS.ROC], payload?.m0100)) {
            // A-consistency-3590 If M0102_PHYSN_ORDRD_SOCROC_DT is not equal to [^] (contains a valid date), then if M0104_PHYSN_RFRL_DT is active it must equal [^].
            if (payload?.m0102 && payload.m0104) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-3590 If M0102 is NOT empty, then M0104 must be empty.`,
                    path: ['m0104'],
                });
            }
            // A-consistency-3600 If M0102_PHYSN_ORDRD_SOCROC_DT=[^] then if M0104_PHYSN_RFRL_DT is active it must not equal [^].
            if (!payload?.m0102 && !payload?.m0104) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-3600 If M0102 is empty, then M0104 must NOT be empty.`,
                    path: ['m0104'],
                });
            }
        }
        if (isActive(SectionA.m1005, version, payload?.m0100) && isTimepoint([OASIS.SOC, OASIS.ROC], payload?.m0100)) {
            // A-consistency-3450 If M1005_INP_DSCHG_UNKNOWN=[0], then if M1005_INP_DISCHARGE_DT is active it must not equal [^].
            if (!payload?.m1005NA && !payload?.m1005 && !payload?.m1000?.includes('DC_NONE_14_DA')) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-3450 Please enter M1005 if known, otherwise select 'Inpatient discharge date unknown'`,
                    path: ['m1005'],
                });
            }
            // A-consistency-3460 If M1005_INP_DSCHG_UNKNOWN=[1], then if M1005_INP_DISCHARGE_DT is active it must equal [^].
            if (payload?.m1005NA && payload?.m1005) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-3460 If M1005 is NOT known, then M1005 must be empty',
                    path: ['m1005'],
                });
            }
            // A-consistency-5320 If M1000_DC_NONE_14_DA=[0], then if M1005_INP_DSCHG_UNKNOWN is active it must not equal [^].
            if (((isActive(SectionA.m1000, version, payload?.m0100) && !payload?.m1000?.length) ||
                !payload?.m1000?.includes('DC_NONE_14_DA')) &&
                !payload?.m1005) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-5320 If NA is NOT checked in M1000, then M1005 must NOT be empty.`,
                    path: ['m1005'],
                });
            }
        }
        if (isActive(SectionA.m2310, version, payload?.m0100) && isTimepoint([OASIS.TR, OASIS.TRD, OASIS.DSCH], payload?.m0100)) {
            const m2310 = payload?.m2310;
            // A-consistency-4550 If M2310_ECR_UNKNOWN=[0], then at least one active item from M2310_ECR_MEDICATION through M2310_ECR_OTHER must equal [1].
            if ((!m2310 || !m2310.length) && payload?.m2301 && !['00', 'UK'].includes(payload?.m2301)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-4550 If 'Reason unknown' is NOT checked, then at least one item above it must be checked.`,
                    path: ['m2310'],
                });
            }
            // A-consistency-4560 If M2310_ECR_UNKNOWN=[1], then all active items from M2310_ECR_MEDICATION through M2310_ECR_OTHER must equal [0].
            if (fieldSetValidate({
                input: m2310,
                ifChecked: ['ECR_UNKNOWN'],
                notChecked: ['ECR_MEDICATION', 'ECR_HYPOGLYC', 'ECR_OTHER'],
            }) &&
                payload?.m2301 &&
                !['00', 'UK'].includes(payload?.m2301)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-4560 If 'Reason unknown' is checked, then all items above it must NOT be checked.`,
                    path: ['m2310'],
                });
            }
        }
        if (isActive(SectionA.a1005, version, payload?.m0100) && isTimepoint([OASIS.SOC], payload?.m0100)) {
            const a1005 = payload?.a1005;
            // A-consistency-5720 (a) At least one active item from A1005A to A1005Y must equal [1].
            if (!a1005?.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5720 (a) At least one item in A1005 from A to Y must be checked',
                    path: ['a1005'],
                });
            }
            // A-consistency-5720 (b) If A1005A=[1], then all active items from A1005B through A1005E must equal [0], and A1005Y must equal [0].
            if (fieldSetValidate({ input: a1005, ifChecked: ['A'], notChecked: ['B', 'C', 'D', 'E', 'Y'] })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5720 (b) If A is checked, then all items from B through E and Y must NOT be checked.',
                    path: ['a1005'],
                });
            }
            // A-consistency-5720 (c) If A1005B=[1] or A1005C=[1] or A1005D=[1] or A1005E=[1], then A1005A must equal [0] and A1005Y must equal [0].
            if (fieldSetValidate({ input: a1005, ifChecked: ['B', 'C', 'D', 'E'], notChecked: ['A', 'Y'] })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5720 (c) If B or C or D or E is checked, then A and Y must NOT be checked.',
                    path: ['a1005'],
                });
            }
            // A-consistency-5720 (d) If A1005X=[1], then A1005Y must equal [0].
            if (fieldSetValidate({ input: a1005, ifChecked: ['X'], notChecked: ['Y'] })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5720 (d) If X is checked, then Y must NOT be checked.',
                    path: ['a1005'],
                });
            }
            // A-consistency-5720 (e) If A1005Y=[1], then all active items from A1005A to A1005X must equal [0].
            if (fieldSetValidate({ input: a1005, ifChecked: ['Y'], notChecked: ['A', 'B', 'C', 'D', 'E', 'X'] })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5720 (e) If Y is checked, then all items from A to X must NOT be checked.',
                    path: ['a1005'],
                });
            }
        }
        if (isActive(SectionA.a1010, version, payload?.m0100) && isTimepoint([OASIS.SOC], payload?.m0100)) {
            const a1010 = payload?.a1010;
            // A-consistency-5730 (a) At least one active item from A1010A through A1010Z must equal [1].
            if (!a1010?.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5730 (a) At least one item from A through Z must be checked.',
                    path: ['a1010'],
                });
            }
            // A-consistency-5730 (b) If any item from A1010A through A1010N=[1], then A1010Y must equal [0] and A1010Z must equal [0].
            if (fieldSetValidate({
                input: a1010,
                ifChecked: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'],
                notChecked: ['Y', 'Z'],
            })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5730 (b) If any item from A through N is checked then Y and Z must NOT be checked.',
                    path: ['a1010'],
                });
            }
            // A-consistency-5730 (c) If A1010X=[1], then A1010Y must equal [0].
            if (fieldSetValidate({ input: a1010, ifChecked: ['X'], notChecked: ['Y'] })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5730 (c) If X is checked then Y must NOT be checked.',
                    path: ['a1010'],
                });
            }
            // A-consistency-5730 (d) If A1010Y=[1], then all active items from A1010A to A1010X must equal [0], and A1010Z must equal [0].
            if (fieldSetValidate({
                input: a1010,
                ifChecked: ['Y'],
                notChecked: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'X', 'Z'],
            })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5730 (d) If Y is checked then all items from A to X and Z must NOT be checked.',
                    path: ['a1010'],
                });
            }
            // A-consistency-5730 (e) If A1010Z=[1], then all active items from A1010A to A1010N must equal [0], and A1010Y must equal [0].
            if (fieldSetValidate({
                input: a1010,
                ifChecked: ['Z'],
                notChecked: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'X', 'Y'],
            })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5730 (e) If Z is checked then all items from A to N and Y must NOT be checked.',
                    path: ['a1010'],
                });
            }
        }
        if (isActive(SectionA.a1250, version, payload?.m0100) && isTimepoint([OASIS.SOC, OASIS.ROC, OASIS.DSCH], payload?.m0100)) {
            const a1250 = payload?.a1250;
            // A-consistency-5740 (a) At least one active item from A1250A to A1250Y must equal [1].
            if (!a1250) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5740 (a) At least one item from A to Y must be checked.',
                    path: ['a1250'],
                });
            }
            // A-consistency-5740 (b) If A1250A=[1] or A1250B=[1], then A1250C and A1250Y must equal [0].
            if (fieldSetValidate({ input: a1250, ifChecked: ['A', 'B'], notChecked: ['C', 'Y'] })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5730 (b) If A or B is checked, then C and Y must not be checked.',
                    path: ['a1250'],
                });
            }
            // A-consistency-5740 (c) If A1250C=[1], then A1250A and A1250B must equal [0].
            if (fieldSetValidate({ input: a1250, ifChecked: ['C'], notChecked: ['A', 'B'] })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5730 (c) If C is checked, then A and B must NOT be checked.',
                    path: ['a1250'],
                });
            }
            // A-consistency-5740 (d) If A1250X=[1], then A1250Y must equal [0].
            if (fieldSetValidate({ input: a1250, ifChecked: ['X'], notChecked: ['Y'] })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5730 (d) If X is checked, then Y must NOT be checked.',
                    path: ['a1250'],
                });
            }
            // A-consistency-5740 (e) If A1250Y=[1], then the following items must equal [0]:  A1250A, A1250B, A1250C, A1250X.
            if (fieldSetValidate({ input: a1250, ifChecked: ['Y'], notChecked: ['A', 'B', 'C', 'X'] })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: '-5730 (e) If Y is checked, then A, B, C, and X must NOT be checked.',
                    path: ['a1250'],
                });
            }
        }
        if (isActive(SectionA.m1000, version, payload?.m0100) && isTimepoint([OASIS.SOC, OASIS.ROC], payload?.m0100)) {
            const m1000 = payload?.m1000;
            // A-consistency-3650 If M1000_DC_NONE_14_DA=[0], then at least one active item from M1000_DC_LTC_14_DA through M1000_DC_OTH_14_DA must equal [1].
            if (!m1000?.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-3650 If 'NA' is NOT checked, then at least one item from 1 through 7 must be checked.`,
                    path: ['m1000'],
                });
            }
            // A-consistency-3660 If M1000_DC_NONE_14_DA=[1], then all active items from M1000_DC_LTC_14_DA through M1000_DC_OTH_14_DA must equal [0].
            if (fieldSetValidate({
                input: m1000,
                ifChecked: ['DC_NONE_14_DA'],
                notChecked: [
                    'DC_LTC_14_DA',
                    'DC_SNF_14_DA',
                    'DC_IPPS_14_DA',
                    'DC_LTCH_14_DA',
                    'DC_IRF_14_DA',
                    'DC_PSYCH_14_DA',
                    'DC_OTH_14_DA',
                ],
            })) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-3660 If 'NA' is checked, then all items from 1 through 7 must NOT be checked.`,
                    path: ['m1000'],
                });
            }
        }
    }
    else {
        // field level
        // A-consistency-3390 PATIENT AGE
        if (field === 'm0066') {
            const payload = input;
            if (payload?.length === 10) {
                const dateDiff = differenceInYears(new UTCDate(), new UTCDate(payload));
                // a) Patient age at the time the assessment was completed cannot be more than 140 years.
                if (dateDiff > 140) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `-3390 (a) Patient age at the time the assessment was completed cannot be more than 140 years. Difference ${dateDiff} years`,
                        path: [],
                    });
                }
                // (b) Patient age at the time the assessment was completed cannot be less than 18 years.
                if (dateDiff < 18) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `-3390 (b) Patient age at the time the assessment was completed cannot be less than 18 years. Difference ${dateDiff} years`,
                        path: [],
                    });
                }
                // c) If M0066_PAT_BIRTH_DT is equal to [--------], then age cannot be computed and this edit does not apply.
                // Patient age is computed as follows:
            }
        }
        // A-consistency-3640 Both M0150_CPAY_NONE and M0150_CPAY_UK must equal [0].
        if (field === 'm0150') {
            const payload = input;
            if (payload && payload.some((p) => ['CPAY_UK'].includes(p)) && payload.some((p) => ['CPAY_NONE'].includes(p))) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `-3640 Both 'None' and 'Unknown' must NOT be checked`,
                    path: [],
                });
            }
        }
    }
};
// A-consistency-5720
// (a) At least one active item from A1005A to A1005Y must equal [1].
// (b) If A1005A=[1], then all active items from A1005B through A1005E must equal [0], and A1005Y must equal [0].
// (c) If A1005B=[1] or A1005C=[1] or A1005D=[1] or A1005E=[1], then A1005A must equal [0] and A1005Y must equal [0].
// (d) If A1005X=[1], then A1005Y must equal [0].
// (e) If A1005Y=[1], then all active items from A1005A to A1005X must equal [0].
// A-consistency-5730
// (a) At least one active item from A1010A through A1010Z must equal [1].
// (b) If any item from A1010A through A1010N=[1], then A1010Y must equal [0] and A1010Z must equal [0].
// (c) If A1010X=[1], then A1010Y must equal [0].
// (d) If A1010Y=[1], then all active items from A1010A to A1010X must equal [0], and A1010Z must equal [0].
// (e) If A1010Z=[1], then all active items from A1010A to A1010N must equal [0], and A1010Y must equal [0].
// A-consistency-5740
// (a) At least one active item from A1250A to A1250Y must equal [1].
// (b) If A1250A=[1] or A1250B=[1], then A1250C and A1250Y must equal [0].
// (c) If A1250C=[1], then A1250A and A1250B must equal [0].
// (d) If A1250X=[1], then A1250Y must equal [0].
// (e) If A1250Y=[1], then the following items must equal [0]:  A1250A, A1250B, A1250C, A1250X.
// A-consistency-5780
// (a) If A2123=[0], then all active items from A2124A through A2124E must equal [^].
// (b) If A2123=[1], then all active item from A2124A through A2124E must not equal [^].
// (c) If A2123=[1], then at least one active item from A2124A through A2124E must equal [1].
// A-consistency-4690 If the assessment was performed by an HHA which has no branches or by a subunit which has no branches, then M0016_BRANCH_ID must contain [N]. This indicates that the assessment was completed by an HHA or subunit which has no branches.
// A-consistency-4700 If the assessment was performed by the home office of an HHA which has branches or by the home office of a subunit which has branches, then M0016_BRANCH_ID must contain [P]. This indicates that the assessment was completed by the home office (an HHA parent or subunit -- not a branch).
// A-consistency-4710 If M0016_BRANCH_ID does not contain a standard branch ID, [N], or [P], a fatal error will result and the record will be rejected.  If a standard branch ID (i.e., not [N,P]) is submitted, then it will be checked against the system table of the HHA’s or subunit’s current and past branch IDs.  If the submitted branch ID does not match a current or past branch ID, then the assessment record will be rejected (i.e., it will result in a fatal error).
// A-consistency-3410 If M0016_BRANCH_ID contains a standard branch ID (i.e., is not equal to [N,P]), then M0014_BRANCH_STATE must not equal [^].
// A-consistency-3420 If M0016_BRANCH_ID is equal to [N,P], then M0014_BRANCH_STATE must equal [^].
// A-consistency-3470 If M0018_PHYSICIAN_UK=[0], then if M0018_PHYSICIAN_ID is active it cannot equal [^].
// A-consistency-3480 If M0018_PHYSICIAN_UK=[1], then if M0018_PHYSICIAN_ID is active it must equal [^].
// A-consistency-3190 In order to modify or inactivate a record that has previously been accepted by the system, the system must be able to locate the previous record.  Appropriate values from the record to be corrected must therefore be submitted for the following locator items:
// M0030_START_CARE_DT
// M0032_ROC_DT
// M0032_ROC_DT_NA
// M0040_PAT_FNAME
// M0040_PAT_LNAME
// M0064_SSN
// M0064_SSN_UK
// M0066_PAT_BIRTH_DT
// M0069_PAT_GENDER
// M0090_INFO_COMPLETED_DT
// M0100_ASSMT_REASON
// M0906_DC_TRAN_DTH_DT
// If a matching previously accepted record cannot be located, a fatal error will result and the submitted record will be rejected.
// A-consistency-3280 If M0100_ASSMT_REASON=[01] and if M0030_START_CARE_DT and M1005_INP_DISCHARGE_DT are both active and contain valid dates (not [^]), then M0030_START_CARE_DT minus M1005_INP_DISCHARGE_DT should be greater than or equal to zero and less than or equal to 14 days.
// A-consistency-3300 If M0100_ASSMT_REASON=[01] and if M0090_INFO_COMPLETED_DT and M0030_START_CARE_DT are both active and contain valid dates (not [^]), then M0090_INFO_COMPLETED_DT minus M0030_START_CARE_DT should be greater than or equal to zero and less than or equal to 5.
// A-consistency-5340 If M0104_PHYSN_RFRL_DT is not equal to [^] and if M0100_ASSMT_REASON=[01], then M0104_PHYSN_RFRL_DT must be less than or equal to M0030_START_CARE_DT and must be greater than M0066_PAT_BIRTH_DT.
// A-consistency-3490 If M0032_ROC_DT_NA=[0], then if M0032_ROC_DT is active it must not equal [^].
// A-consistency-3500 If M0032_ROC_DT_NA=[1], then if M0032_ROC_DT is active it must equal [^].
// A-consistency-4730 If M0100_ASSMT_REASON=[01], then M0032_ROC_DT_NA must be [1].
// A-consistency-4740 If M0100_ASSMT_REASON=[03], then M0032_ROC_DT_NA must be [0].
// A-consistency-3510 If M0063_MEDICARE_NA=[0], then if M0063_MEDICARE_NUM is active it must not equal [^].
// A-consistency-3520 If M0063_MEDICARE_NA=[1], then if M0063_MEDICARE_NUM is active it must equal [^].
// A-consistency-3530 If M0064_SSN_UK=[0], then if M0064_SSN is active it must not equal [^].
// A-consistency-3540 If M0064_SSN_UK=[1], then if M0064_SSN is active it must equal [^].
// A-consistency-3550 If M0065_MEDICAID_NA=[0], then if M0065_MEDICAID_NUM is active it must not equal [^].
// A-consistency-3560 If M0065_MEDICAID_NA=[1], then if M0065_MEDICAID_NUM is active it must equal [^].
// A-consistency-4570 If M0100_ASSMT_REASON=[09], then if M2410_INPAT_FACILITY is active it must equal [NA].
// A-consistency-4580 If M0100_ASSMT_REASON=[06,07], then if M2410_INPAT_FACILITY is active it must not equal [NA].
// A-consistency-3290 If M0100_ASSMT_REASON=[03] and if M0032_ROC_DT and  M1005_INP_DISCHARGE_DT are both active and contain valid dates (not [^]), then M0032_ROC_DT minus  M1005_INP_DISCHARGE_DT should be greater than or equal to zero and less than or equal to 14.
// A-consistency-5290 If M0100_ASSMT_REASON=[06,07,08,09], then M0906_DC_TRAN_DTH_DT must not equal [^].
// A-consistency-5300 If TRANS_TYPE_CD=[3] (inactivate existing record) and M0100_ASSMT_REASON=[01,03,04,05], then M0906_DC_TRAN_DTH_DT must equal [^].
// A-consistency-3310 If M0100_ASSMT_REASON=[03] and if M0090_INFO_COMPLETED_DT and M0032_ROC_DT are both active and contain valid dates (not [^]), then M0090_INFO_COMPLETED_DT minus M0032_ROC_DT should be greater than or equal to zero and less than or equal to 2.
// A-consistency-3320 If M0100_ASSMT_REASON=[09] and if M0090_INFO_COMPLETED_DT and M0906_DC_TRAN_DTH_DT are both active and contain valid dates (not [^]), then M0090_INFO_COMPLETED_DT minus M0906_DC_TRAN_DTH_DT should be greater than or equal to zero and less than or equal to 2.
// A-consistency-5350 If M0104_PHYSN_RFRL_DT is not equal to [^] and if M0100_ASSMT_REASON=[03], then M0104_PHYSN_RFRL_DT must be less than or equal to M0032_ROC_DT and must be greater than M0066_PAT_BIRTH_DT.
// A-consistency-3570 If M0102_PHYSN_ORDRD_SOCROC_DT is not equal to [^] (contains a valid date), then if M0102_PHYSN_ORDRD_SOCROC_DT_NA is active it must equal [0,^].
// A-consistency-3580 If M0102_PHYSN_ORDRD_SOCROC_DT=[^], then if M0102_PHYSN_ORDRD_SOCROC_DT_NA is active it must equal [1].
// A-consistency-4860 If M0150_CPAY_MCAID_FFS=[1], then M0065_MEDICAID_NUM must not equal [^].
// A-consistency-4850 If M0150_CPAY_MCARE_FFS=[1], then M0063_MEDICARE_NUM must not equal [^].
// A-consistency-5460 The value for M0090_INFO_COMPLETED_DT must be within the allowable date range specified by the value for SPEC_VRSN_CD. The list of SPEC_VRSN_CD values and their corresponding date ranges are provided in the Overview document for the OASIS Data Specifications.
// A-consistency-3170 The value submitted for M0010_CCN (provider's CMS Certification Number -- CCN) will be compared with the value that is currently in the National Database.  If the values do not match, a warning will be issued.
// A-consistency-3390 PATIENT AGE
// a) Patient age at the time the assessment was completed cannot be more than 140 years.
// b) Patient age at the time the assessment was completed cannot be less than 18 years.
// c) If M0066_PAT_BIRTH_DT is equal to [--------], then age cannot be computed and this edit does not apply.
// Patient age is computed as follows:
// 1) If both the month and day in M0066_PAT_BIRTH_DT are unknown, replace the month with \"07\" and the day with \"02\" for the purpose of calculating age.
// 2) If only the day in M0066_PAT_BIRTH_DT is unknown, replace the day with the \"15\" for the purpose of calculating age.
// 3) Provisionally compute age (in years) by subtracting the year in M0066_PAT_BIRTH_DT from the year in M0090_INFO_COMPLETED_DT.
// 4) If the month in M0090_INFO_COMPLETED_DT is less than the month in M0066_PAT_BIRTH_DT, then subtract 1 from the provisional age computed in (3) above.
// 5) If the month in M0090_INFO_COMPLETED_DT is equal to the month in M0066_PAT_BIRTH_DT and if the day in M0090_INFO_COMPLETED_DT is less than the day in M0066_PAT_BIRTH_DT, then subtract 1 from the provisional age computed in (3) above.
// A-consistency-3590 If M0102_PHYSN_ORDRD_SOCROC_DT is not equal to [^] (contains a valid date), then if M0104_PHYSN_RFRL_DT is active it must equal [^].
// A-consistency-3600 If M0102_PHYSN_ORDRD_SOCROC_DT=[^] then if M0104_PHYSN_RFRL_DT is active it must not equal [^].
// A-consistency-3640 Both M0150_CPAY_NONE and M0150_CPAY_UK must equal [0].
// A-consistency-3650 If M1000_DC_NONE_14_DA=[0], then at least one active item from M1000_DC_LTC_14_DA through M1000_DC_OTH_14_DA must equal [1].
// A-consistency-3660 If M1000_DC_NONE_14_DA=[1], then all active items from M1000_DC_LTC_14_DA through M1000_DC_OTH_14_DA must equal [0].
// A-consistency-5320 If M1000_DC_NONE_14_DA=[0], then if M1005_INP_DSCHG_UNKNOWN is active it must not equal [^].
// A-consistency-3450 If M1005_INP_DSCHG_UNKNOWN=[0], then if M1005_INP_DISCHARGE_DT is active it must not equal [^].
// A-consistency-3460 If M1005_INP_DSCHG_UNKNOWN=[1], then if M1005_INP_DISCHARGE_DT is active it must equal [^].
// A-consistency-4550 If M2310_ECR_UNKNOWN=[0], then at least one active item from M2310_ECR_MEDICATION through M2310_ECR_OTHER must equal [1].
// A-consistency-4560 If M2310_ECR_UNKNOWN=[1], then all active items from M2310_ECR_MEDICATION through M2310_ECR_OTHER must equal [0].
